import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { FolderKanban, AlertCircle, Users, Settings as SettingsIcon, Mail, Tags, Clock, Save, Link2, Link2Off, RefreshCw, ChevronDown, Copy, CheckCheck, X, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
    getTimeSettings,
    updateTimeSettings,
    getFortnoxStatus,
    putFortnoxClientConfig,
    getFortnoxAuthStart,
    postFortnoxAuthRevoke,
    getFortnoxSettings,
    putFortnoxSettings,
    getFortnoxEmployeeMappings,
    putFortnoxEmployeeMappings,
    getFortnoxCustomerMappings,
    putFortnoxCustomerMappings,
} from '../../lib/api';
import ProjectsPage from './ProjectsPage';
import CostCodesPage from './CostCodesPage';
import DeviationsPage from './DeviationsPage';
import EmployeesPage from './EmployeesPage';
import ExpenseSettingsPage from './ExpenseSettingsPage';
import EmailNotificationsPage from './EmailNotificationsPage';
import { useAuth } from '../../context/AuthContext';

// The one settings section an accountant is entitled to (Task 22.9). Named
// rather than inlined so the guard below and the tab it pins to cannot drift
// apart.
const ACCOUNTANT_TAB = 'fortnox-mapping';

function TimePolicyTab() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dailyBaseHours, setDailyBaseHours] = useState(8);
    const [taxRate, setTaxRate] = useState(31.42);
    const [payslipAutoEmail, setPayslipAutoEmail] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const res = await getTimeSettings();
                if (res.data?.daily_base_hours != null) {
                    setDailyBaseHours(res.data.daily_base_hours);
                }
                setTaxRate(parseFloat(((res.data?.employer_tax_rate ?? 0.3142) * 100).toFixed(4)));
                setPayslipAutoEmail(res.data?.payslip_auto_email ?? false);
            } catch {
                toast.error(t('settings.time_policy_failed_load'));
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [t]);

    const handleSave = async () => {
        const val = parseFloat(dailyBaseHours);
        if (isNaN(val) || val < 1 || val > 24) {
            toast.error(t('settings.time_policy_failed_save'));
            return;
        }
        setSaving(true);
        try {
            await updateTimeSettings({
                daily_base_hours: val,
                employer_tax_rate: parseFloat(taxRate) / 100,
                payslip_auto_email: payslipAutoEmail,
            });
            toast.success(t('settings.time_policy_saved'));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('settings.time_policy_failed_save'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="time-policy-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        {t('settings.payroll_settings_title')}
                    </CardTitle>
                    <CardDescription>
                        {t('settings.daily_base_hours_hint')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="max-w-xs">
                        <Label className="text-xs uppercase mb-2 block text-muted-foreground">
                            {t('settings.daily_base_hours_label')}
                        </Label>
                        <div className="relative">
                            <Input
                                type="number"
                                step="0.5"
                                min="1"
                                max="24"
                                value={dailyBaseHours}
                                onChange={(e) => setDailyBaseHours(e.target.value)}
                                className="pr-8"
                                data-testid="daily-base-hours-input"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">h</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('settings.daily_base_hours_hint')}
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="employer-tax-rate">{t('settings.employer_tax_rate_label')}</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                id="employer-tax-rate"
                                type="number"
                                step="0.01"
                                min="0"
                                max="200"
                                value={taxRate}
                                onChange={(e) => setTaxRate(e.target.value)}
                                className="w-32"
                                data-testid="employer-tax-rate-input"
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{t('settings.employer_tax_rate_hint')}</p>
                    </div>
                    <div className="space-y-1.5 pt-2 border-t">
                        <div className="flex items-center gap-3">
                            <Switch
                                id="payslip-auto-email"
                                checked={payslipAutoEmail}
                                onCheckedChange={setPayslipAutoEmail}
                                data-testid="payslip-auto-email-switch"
                            />
                            <Label htmlFor="payslip-auto-email" className="text-sm cursor-pointer">
                                {t('settings.payslip_auto_email_label')}
                            </Label>
                        </div>
                        <p className="text-xs text-muted-foreground ml-10">
                            {t('settings.payslip_auto_email_hint')}
                        </p>
                    </div>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-time-policy-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('common.saving') : t('common.save')}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

// ─── Fortnox Settings Tab ────────────────────────────────────────────────────

function FortnoxTab({ configOnly = false, mappingOnly = false, readOnly = false }) {
    // When neither flag is set, show everything (used from the full settings tab bar)
    const showConfig = !mappingOnly;
    const showMapping = !configOnly;
    // `readOnly` is set for a viewer who may READ the mappings but not write
    // them — today that is exactly the accountant (Task 22.9). Both mapping
    // PUTs are require_owner and are not being widened, so their Save buttons
    // could only ever 403. The tables still render in full: an accountant runs
    // the salary, absence and billing syncs, all of which are driven by these
    // mappings, so seeing what the sync will do is the point of the page.
    // Admins and owners pass readOnly=false and are unaffected.
    const { t } = useTranslation();

    // ── Connection state ──
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [savingCredentials, setSavingCredentials] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    // OAuth error from callback redirect (structured, persists until dismissed)
    const [fortnoxOAuthError, setFortnoxOAuthError] = useState(null);
    // copy-to-clipboard feedback for callback URL
    const [callbackUrlCopied, setCallbackUrlCopied] = useState(false);

    // ── Settings state ──
    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);

    // Salary codes
    const [salaryCodes, setSalaryCodes] = useState({
        regular: '', standard_ot: '', special_ot: '', flex_save: '', mileage: '', allowance: '',
    });
    const [savingSalary, setSavingSalary] = useState(false);

    // Absence codes
    const [absenceCodes, setAbsenceCodes] = useState({
        SJK: '', VAB: '', SEM: '', PAP: '', custom1: '', custom2: '',
    });
    const [savingAbsence, setSavingAbsence] = useState(false);

    // GL accounts
    const [glAccounts, setGlAccounts] = useState({
        expense_debit: '', employee_payable: '', revenue_account: '', accounts_receivable: '',
    });
    const [savingGl, setSavingGl] = useState(false);

    // Invoice settings
    const [invoiceSettings, setInvoiceSettings] = useState({
        vat_rate: 25, terms_of_payment: '', way_of_delivery: '', create_as_draft: true,
    });
    const [savingInvoice, setSavingInvoice] = useState(false);

    // Sync toggles
    const [syncSettings, setSyncSettings] = useState({
        sync_salary: true, sync_absences: true, sync_expenses: true,
        sync_billing: true, sync_receipts: true, auto_sync_enabled: false,
    });

    // Employee mappings
    const [employeeMappings, setEmployeeMappings] = useState([]);
    const [mappingsLoading, setMappingsLoading] = useState(true);
    const [savingEmployees, setSavingEmployees] = useState(false);

    // Customer mappings
    const [customerMappings, setCustomerMappings] = useState([]);
    const [customerMappingsLoading, setCustomerMappingsLoading] = useState(true);
    const [savingCustomers, setSavingCustomers] = useState(false);

    // Accordion state for mappingOnly view
    const [empMappingOpen, setEmpMappingOpen] = useState(true);
    const [custMappingOpen, setCustMappingOpen] = useState(false);
    // No syncSettingsOpen — the sync-settings accordion is gone from the
    // mappingOnly view entirely (Task 22.9); everywhere else the section is
    // always expanded.

    // ── Load status ──
    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const res = await getFortnoxStatus();
            setStatus(res.data);
        } catch {
            // Status endpoint may 503 if Fortnox is not configured — treat as disconnected
            setStatus({ connected: false, client_configured: false });
        } finally {
            setStatusLoading(false);
        }
    }, []);

    // ── Load settings ──
    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const res = await getFortnoxSettings();
            const s = res.data;
            setSettings(s);
            if (s.salary_code_mappings) setSalaryCodes(prev => ({ ...prev, ...s.salary_code_mappings }));
            if (s.absence_code_mappings) setAbsenceCodes(prev => ({ ...prev, ...s.absence_code_mappings }));
            if (s.gl_accounts) setGlAccounts(prev => ({ ...prev, ...s.gl_accounts }));
            if (s.invoice_settings) setInvoiceSettings(prev => ({ ...prev, ...s.invoice_settings }));
            setSyncSettings({
                sync_salary: s.sync_salary ?? true,
                sync_absences: s.sync_absences ?? true,
                sync_expenses: s.sync_expenses ?? true,
                sync_billing: s.sync_billing ?? true,
                sync_receipts: s.sync_receipts ?? true,
                auto_sync_enabled: s.auto_sync_enabled ?? false,
            });
        } catch {
            toast.error(t('settings.fortnox.load_error'));
        } finally {
            setSettingsLoading(false);
        }
    // intentionally omit derived state from deps — we only need t
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    // ── Load mappings ──
    const loadMappings = useCallback(async () => {
        setMappingsLoading(true);
        setCustomerMappingsLoading(true);
        try {
            const [empRes, custRes] = await Promise.all([
                getFortnoxEmployeeMappings(),
                getFortnoxCustomerMappings(),
            ]);
            setEmployeeMappings(empRes.data ?? []);
            setCustomerMappings(custRes.data ?? []);
        } catch {
            // Non-fatal — mappings may be empty before first sync
        } finally {
            setMappingsLoading(false);
            setCustomerMappingsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
        loadSettings();
        loadMappings();
    }, [loadStatus, loadSettings, loadMappings]);

    // Map known Fortnox OAuth error codes to i18n keys.
    const _FORTNOX_ERROR_MAP = {
        invalid_scope: 'settings.fortnox.error_invalid_scope',
        access_denied: 'settings.fortnox.error_access_denied',
        redirect_uri_mismatch: 'settings.fortnox.error_redirect_mismatch',
        invalid_request: 'settings.fortnox.error_invalid_request',
        unauthorized_client: 'settings.fortnox.error_unauthorized_client',
        unsupported_response_type: 'settings.fortnox.error_unsupported_response',
        server_error: 'settings.fortnox.error_server_error',
        temporarily_unavailable: 'settings.fortnox.error_temporarily_unavailable',
    };

    // Read OAuth error from the Fortnox callback redirect on mount.
    // The outer AdminSettingsPage switches to this tab before we mount,
    // so by the time this effect runs the URL params are still present.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('fortnox') === 'error') {
            const reason = params.get('reason') || '';
            const i18nKey = _FORTNOX_ERROR_MAP[reason] || 'settings.fortnox.error_unknown';
            setFortnoxOAuthError({ reason, i18nKey });
            toast.error(t('settings.fortnox.connected_error'));
            window.history.replaceState({}, '', window.location.pathname);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Handlers ──

    const handleSaveCredentials = async () => {
        setSavingCredentials(true);
        try {
            await putFortnoxClientConfig({ client_id: clientId, client_secret: clientSecret });
            toast.success(t('settings.fortnox.credentials_saved'));
            await loadStatus();
        } catch {
            toast.error(t('settings.fortnox.credentials_error'));
        } finally {
            setSavingCredentials(false);
        }
    };

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const res = await getFortnoxAuthStart();
            if (res.data?.auth_url) {
                // Navigate in the same window — modern OAuth pattern, works on
                // mobile, and avoids popup-blocker issues.
                window.location.href = res.data.auth_url;
                // Note: setConnecting(false) is intentionally omitted from the
                // success path — the page is navigating away so state teardown
                // is moot. The finally block fires before navigation completes.
            }
        } catch {
            toast.error(t('settings.fortnox.connected_error'));
            setConnecting(false);
        }
    };

    const handleCopyCallbackUrl = async () => {
        const url = status?.callback_url;
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCallbackUrlCopied(true);
            setTimeout(() => setCallbackUrlCopied(false), 2000);
        } catch {
            toast.error(t('settings.fortnox.copy_error'));
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm(t('settings.fortnox.disconnect_confirm'))) return;
        setDisconnecting(true);
        try {
            await postFortnoxAuthRevoke();
            toast.success(t('settings.fortnox.disconnect_success'));
            await loadStatus();
        } catch {
            toast.error(t('settings.fortnox.disconnect_error'));
        } finally {
            setDisconnecting(false);
        }
    };

    const handleSaveSalaryCodes = async () => {
        setSavingSalary(true);
        try {
            await putFortnoxSettings({ salary_code_mappings: salaryCodes });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_error'));
        } finally {
            setSavingSalary(false);
        }
    };

    const handleSaveAbsenceCodes = async () => {
        setSavingAbsence(true);
        try {
            await putFortnoxSettings({ absence_code_mappings: absenceCodes });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_error'));
        } finally {
            setSavingAbsence(false);
        }
    };

    const handleSaveGlAccounts = async () => {
        setSavingGl(true);
        try {
            await putFortnoxSettings({ gl_accounts: glAccounts });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_error'));
        } finally {
            setSavingGl(false);
        }
    };

    const handleSaveInvoice = async () => {
        setSavingInvoice(true);
        try {
            await putFortnoxSettings({ invoice_settings: invoiceSettings });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_error'));
        } finally {
            setSavingInvoice(false);
        }
    };

    const handleSyncToggle = async (key, value) => {
        const next = { ...syncSettings, [key]: value };
        setSyncSettings(next);
        try {
            await putFortnoxSettings({ [key]: value });
        } catch {
            setSyncSettings(syncSettings);
            toast.error(t('settings.fortnox.save_error'));
        }
    };

    const handleSaveEmployeeMappings = async () => {
        setSavingEmployees(true);
        try {
            // user_EMAIL, not user_id (Task 22.9). The mapping collection is
            // keyed by email end to end: GET /fortnox/employee-mappings returns
            // {user_email, user_name, fortnox_employee_id} and never a user_id,
            // and FortnoxEmployeeMappingItem requires a non-optional
            // user_email. Reading `user_id` off those rows produced undefined,
            // JSON.stringify dropped the key, and every save 422'd behind the
            // generic error toast — this table has never once saved.
            const mappings = employeeMappings.map(({ user_email, fortnox_employee_id }) => ({
                user_email,
                fortnox_employee_id,
            }));
            await putFortnoxEmployeeMappings({ mappings });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_mappings_error'));
        } finally {
            setSavingEmployees(false);
        }
    };

    const handleSaveCustomerMappings = async () => {
        setSavingCustomers(true);
        try {
            const mappings = customerMappings.map(({ project_id, fortnox_customer_number }) => ({
                project_id,
                fortnox_customer_number,
            }));
            await putFortnoxCustomerMappings({ mappings });
            toast.success(t('settings.fortnox.saved'));
        } catch {
            toast.error(t('settings.fortnox.save_mappings_error'));
        } finally {
            setSavingCustomers(false);
        }
    };

    // ── Helpers ──
    const formatExpiry = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
    };

    const isConnected = status?.connected;
    const isClientConfigured = status?.client_configured;

    if (statusLoading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="fortnox-tab">

            {/* ── OAuth error Alert (persistent, appears after failed Fortnox callback) ── */}
            {fortnoxOAuthError && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{t('settings.fortnox.connected_error')}</AlertTitle>
                    <AlertDescription className="space-y-2">
                        <p>{t(fortnoxOAuthError.i18nKey, { reason: fortnoxOAuthError.reason })}</p>
                        {fortnoxOAuthError.reason && (
                            <p className="text-xs">
                                <span className="font-medium">{t('settings.fortnox.error_raw_reason_label')}: </span>
                                <code className="bg-destructive/20 px-1 rounded">{fortnoxOAuthError.reason}</code>
                            </p>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFortnoxOAuthError(null)}
                            className="mt-1"
                        >
                            <X className="w-3 h-3 mr-1" />
                            {t('settings.fortnox.error_dismiss')}
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {showConfig && (<>
            {/* ── Section 0: Setup Instructions ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.setup_instructions_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Warning if callback URL is not configured server-side */}
                    {status && !status.callback_url && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{t('settings.fortnox.callback_url_missing')}</AlertDescription>
                        </Alert>
                    )}

                    <ol className="space-y-3 text-sm">
                        <li>
                            <span className="font-medium">1. </span>
                            {t('settings.fortnox.setup_instructions_step1')}{' '}
                            <a
                                href="https://developer.fortnox.se"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-primary inline-flex items-center gap-1"
                            >
                                {t('settings.fortnox.developer_portal_link')}
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        </li>
                        <li><span className="font-medium">2. </span>{t('settings.fortnox.setup_instructions_step2')}</li>
                        <li>
                            <span className="font-medium">3. </span>{t('settings.fortnox.setup_instructions_step3')}
                            {status?.callback_url && (
                                <div className="mt-2 flex items-center gap-2">
                                    <code className="flex-1 bg-muted px-3 py-2 rounded text-xs break-all">
                                        {status.callback_url}
                                    </code>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopyCallbackUrl}
                                        className="shrink-0"
                                        title={t('settings.fortnox.callback_url_copy')}
                                    >
                                        {callbackUrlCopied
                                            ? <CheckCheck className="w-4 h-4" />
                                            : <Copy className="w-4 h-4" />}
                                        <span className="ml-1 hidden sm:inline">
                                            {callbackUrlCopied
                                                ? t('settings.fortnox.callback_url_copied')
                                                : t('settings.fortnox.callback_url_copy')}
                                        </span>
                                    </Button>
                                </div>
                            )}
                        </li>
                        <li>
                            <span className="font-medium">4. </span>{t('settings.fortnox.setup_instructions_step4')}
                            {status?.required_scopes?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {status.required_scopes.map((scope) => (
                                        <Badge key={scope} variant="secondary">{scope}</Badge>
                                    ))}
                                </div>
                            )}
                        </li>
                        <li><span className="font-medium">5. </span>{t('settings.fortnox.setup_instructions_step5')}</li>
                        <li><span className="font-medium">6. </span>{t('settings.fortnox.setup_instructions_step6')}</li>
                        <li><span className="font-medium">7. </span>{t('settings.fortnox.setup_instructions_step7')}</li>
                    </ol>
                </CardContent>
            </Card>

            {/* ── Section 1: Connection ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-primary" />
                        {t('settings.fortnox.connection_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                        <Badge variant={isConnected ? 'default' : 'secondary'}>
                            {isConnected
                                ? t('settings.fortnox.connected')
                                : t('settings.fortnox.disconnected')}
                        </Badge>
                        {isConnected && status.company_name && (
                            <span className="text-sm text-muted-foreground">
                                {t('settings.fortnox.company')}: <strong>{status.company_name}</strong>
                            </span>
                        )}
                    </div>

                    {isConnected && (
                        <div className="text-sm text-muted-foreground space-y-1">
                            {status.scope && (
                                <p>{t('settings.fortnox.scope_label')}: {status.scope}</p>
                            )}
                            {status.token_expiry && (
                                <p>{t('settings.fortnox.token_expiry_label')}: {formatExpiry(status.token_expiry)}</p>
                            )}
                        </div>
                    )}

                    {!isClientConfigured && (
                        <div className="space-y-3 pt-2 border-t">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                        {t('settings.fortnox.client_id_label')}
                                    </Label>
                                    <Input
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        data-testid="fortnox-client-id"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                        {t('settings.fortnox.client_secret_label')}
                                    </Label>
                                    <Input
                                        type="password"
                                        value={clientSecret}
                                        onChange={(e) => setClientSecret(e.target.value)}
                                        placeholder="••••••••"
                                        data-testid="fortnox-client-secret"
                                    />
                                </div>
                            </div>
                            <Button
                                onClick={handleSaveCredentials}
                                disabled={savingCredentials || !clientId || !clientSecret}
                                variant="outline"
                                data-testid="save-credentials-btn"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {savingCredentials
                                    ? t('settings.fortnox.saving_credentials')
                                    : t('settings.fortnox.save_credentials')}
                            </Button>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <Button
                            onClick={handleConnect}
                            disabled={connecting || !isClientConfigured}
                            data-testid="connect-fortnox-btn"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {connecting
                                ? t('settings.fortnox.connecting')
                                : t('settings.fortnox.connect_button')}
                        </Button>
                        {isConnected && (
                            <Button
                                variant="destructive"
                                onClick={handleDisconnect}
                                disabled={disconnecting}
                                data-testid="disconnect-fortnox-btn"
                            >
                                <Link2Off className="w-4 h-4 mr-2" />
                                {disconnecting
                                    ? t('settings.fortnox.disconnecting')
                                    : t('settings.fortnox.disconnect_button')}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* ── Section 2: Salary Code Mapping ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.salary_codes_title')}
                    </CardTitle>
                    <CardDescription>{t('settings.fortnox.salary_code_hint')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {settingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[
                                ['regular', t('settings.fortnox.salary_regular_label')],
                                ['standard_ot', t('settings.fortnox.salary_standard_ot_label')],
                                ['special_ot', t('settings.fortnox.salary_special_ot_label')],
                                ['flex_save', t('settings.fortnox.salary_flex_save_label')],
                                ['mileage', t('settings.fortnox.salary_mileage_label')],
                                ['allowance', t('settings.fortnox.salary_allowance_label')],
                            ].map(([key, label]) => (
                                <div key={key}>
                                    <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                        {label}
                                    </Label>
                                    <Input
                                        value={salaryCodes[key] ?? ''}
                                        onChange={(e) => setSalaryCodes((prev) => ({ ...prev, [key]: e.target.value }))}
                                        placeholder="e.g. 100"
                                        data-testid={`salary-code-${key}`}
                                    />
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {t('settings.fortnox.salary_code_hint')}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                    <Button
                        onClick={handleSaveSalaryCodes}
                        disabled={savingSalary || settingsLoading}
                        data-testid="save-salary-codes-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingSalary ? t('common.saving') : t('common.save')}
                    </Button>
                </CardContent>
            </Card>

            {/* ── Section 3: Absence Code Mapping ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.absence_codes_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {settingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[
                                ['SJK', t('settings.fortnox.absence_sick_label')],
                                ['VAB', t('settings.fortnox.absence_vab_label')],
                                ['SEM', t('settings.fortnox.absence_vacation_label')],
                                ['PAP', t('settings.fortnox.absence_parental_label')],
                                ['custom1', t('settings.fortnox.absence_custom1_label')],
                                ['custom2', t('settings.fortnox.absence_custom2_label')],
                            ].map(([key, label]) => (
                                <div key={key}>
                                    <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                        {label}
                                    </Label>
                                    <Input
                                        value={absenceCodes[key] ?? ''}
                                        onChange={(e) => setAbsenceCodes((prev) => ({ ...prev, [key]: e.target.value }))}
                                        placeholder={key.startsWith('custom') ? '' : key}
                                        data-testid={`absence-code-${key}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <Button
                        onClick={handleSaveAbsenceCodes}
                        disabled={savingAbsence || settingsLoading}
                        data-testid="save-absence-codes-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingAbsence ? t('common.saving') : t('common.save')}
                    </Button>
                </CardContent>
            </Card>

            {/* ── Section 4: GL Accounts ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.gl_accounts_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {settingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                ['expense_debit', t('settings.fortnox.gl_expense_debit_label')],
                                ['employee_payable', t('settings.fortnox.gl_employee_payable_label')],
                                ['revenue_account', t('settings.fortnox.gl_revenue_label')],
                                ['accounts_receivable', t('settings.fortnox.gl_receivable_label')],
                            ].map(([key, label]) => (
                                <div key={key}>
                                    <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                        {label}
                                    </Label>
                                    <Input
                                        value={glAccounts[key] ?? ''}
                                        onChange={(e) => setGlAccounts((prev) => ({ ...prev, [key]: e.target.value }))}
                                        placeholder="e.g. 1510"
                                        data-testid={`gl-${key}`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <Button
                        onClick={handleSaveGlAccounts}
                        disabled={savingGl || settingsLoading}
                        data-testid="save-gl-accounts-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingGl ? t('common.saving') : t('common.save')}
                    </Button>
                </CardContent>
            </Card>

            {/* ── Section 5: Invoice Settings ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.invoice_settings_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {settingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                    {t('settings.fortnox.vat_rate_label')}
                                </Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={invoiceSettings.vat_rate}
                                    onChange={(e) =>
                                        setInvoiceSettings((prev) => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))
                                    }
                                    data-testid="invoice-vat-rate"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                    {t('settings.fortnox.terms_label')}
                                </Label>
                                <Input
                                    value={invoiceSettings.terms_of_payment}
                                    onChange={(e) =>
                                        setInvoiceSettings((prev) => ({ ...prev, terms_of_payment: e.target.value }))
                                    }
                                    placeholder="e.g. 30"
                                    data-testid="invoice-terms"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-1 block text-muted-foreground">
                                    {t('settings.fortnox.delivery_label')}
                                </Label>
                                <Input
                                    value={invoiceSettings.way_of_delivery}
                                    onChange={(e) =>
                                        setInvoiceSettings((prev) => ({ ...prev, way_of_delivery: e.target.value }))
                                    }
                                    placeholder="e.g. Email"
                                    data-testid="invoice-delivery"
                                />
                            </div>
                            <div className="flex items-center gap-3 pt-4">
                                <Switch
                                    id="invoice-draft"
                                    checked={invoiceSettings.create_as_draft}
                                    onCheckedChange={(val) =>
                                        setInvoiceSettings((prev) => ({ ...prev, create_as_draft: val }))
                                    }
                                    data-testid="invoice-draft-switch"
                                />
                                <Label htmlFor="invoice-draft" className="text-sm cursor-pointer">
                                    {t('settings.fortnox.create_as_draft_label')}
                                </Label>
                            </div>
                        </div>
                    )}
                    <Button
                        onClick={handleSaveInvoice}
                        disabled={savingInvoice || settingsLoading}
                        data-testid="save-invoice-btn"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingInvoice ? t('common.saving') : t('common.save')}
                    </Button>
                </CardContent>
            </Card>

            </>)}

            {showMapping && (<>
            {/* ── Section 6: Employee Mapping ── */}
            {mappingOnly && (
                <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 border border-border text-left hover:bg-muted/60 transition-colors"
                    onClick={() => setEmpMappingOpen(p => !p)}
                    aria-expanded={empMappingOpen}
                >
                    <span className="text-sm font-medium">{t('settings.fortnox.employee_mapping_title')}</span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${empMappingOpen ? '' : '-rotate-90'}`} />
                </button>
            )}
            {(!mappingOnly || empMappingOpen) && <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.employee_mapping_title')}
                    </CardTitle>
                    <CardDescription>{t('settings.fortnox.employee_mapping_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {mappingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : employeeMappings.length === 0 ? (
                        <p className="text-sm text-muted-foreground">—</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_name')}
                                        </th>
                                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_email')}
                                        </th>
                                        <th className="text-left py-2 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_fortnox_id')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Keyed by email for the same reason the save
                                        payload is: `user_id` is not a field these
                                        rows have, so every key was the identical
                                        `undefined` and React could not tell one
                                        row from another. */}
                                    {employeeMappings.map((row, idx) => (
                                        <tr key={row.user_email} className="border-b last:border-0">
                                            <td className="py-2 pr-4">{row.user_name}</td>
                                            <td className="py-2 pr-4 text-muted-foreground">{row.user_email}</td>
                                            <td className="py-2">
                                                <Input
                                                    value={row.fortnox_employee_id ?? ''}
                                                    onChange={(e) => {
                                                        const next = [...employeeMappings];
                                                        next[idx] = { ...next[idx], fortnox_employee_id: e.target.value };
                                                        setEmployeeMappings(next);
                                                    }}
                                                    placeholder="e.g. 1"
                                                    className="h-8 w-32"
                                                    readOnly={readOnly}
                                                    aria-label={t('settings.fortnox.col_fortnox_id')}
                                                    data-testid={`employee-mapping-${row.user_email}`}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {readOnly ? (
                        <p className="text-xs text-muted-foreground" data-testid="employee-mapping-read-only">
                            {t('settings.fortnox.mapping_read_only_note')}
                        </p>
                    ) : (
                        <Button
                            onClick={handleSaveEmployeeMappings}
                            disabled={savingEmployees || mappingsLoading}
                            data-testid="save-employee-mappings-btn"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {savingEmployees ? t('common.saving') : t('common.save')}
                        </Button>
                    )}
                </CardContent>
            </Card>}

            {/* ── Section 7: Customer Mapping ── */}
            {mappingOnly && (
                <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 border border-border text-left hover:bg-muted/60 transition-colors"
                    onClick={() => setCustMappingOpen(p => !p)}
                    aria-expanded={custMappingOpen}
                >
                    <span className="text-sm font-medium">{t('settings.fortnox.customer_mapping_title')}</span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${custMappingOpen ? '' : '-rotate-90'}`} />
                </button>
            )}
            {(!mappingOnly || custMappingOpen) && <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.customer_mapping_title')}
                    </CardTitle>
                    <CardDescription>{t('settings.fortnox.customer_mapping_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {customerMappingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : customerMappings.length === 0 ? (
                        <p className="text-sm text-muted-foreground">—</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_project')}
                                        </th>
                                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_project_code')}
                                        </th>
                                        <th className="text-left py-2 font-medium text-muted-foreground uppercase text-xs">
                                            {t('settings.fortnox.col_fortnox_number')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customerMappings.map((row, idx) => (
                                        <tr key={row.project_id} className="border-b last:border-0">
                                            <td className="py-2 pr-4">{row.project_name}</td>
                                            <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">{row.project_code || '—'}</td>
                                            <td className="py-2">
                                                <Input
                                                    value={row.fortnox_customer_number ?? ''}
                                                    onChange={(e) => {
                                                        const next = [...customerMappings];
                                                        next[idx] = { ...next[idx], fortnox_customer_number: e.target.value };
                                                        setCustomerMappings(next);
                                                    }}
                                                    placeholder="e.g. 100"
                                                    className="h-8 w-32"
                                                    readOnly={readOnly}
                                                    aria-label={t('settings.fortnox.col_fortnox_number')}
                                                    data-testid={`customer-mapping-${row.project_id}`}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {readOnly ? (
                        <p className="text-xs text-muted-foreground" data-testid="customer-mapping-read-only">
                            {t('settings.fortnox.mapping_read_only_note')}
                        </p>
                    ) : (
                        <Button
                            onClick={handleSaveCustomerMappings}
                            disabled={savingCustomers || customerMappingsLoading}
                            data-testid="save-customer-mappings-btn"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {savingCustomers ? t('common.saving') : t('common.save')}
                        </Button>
                    )}
                </CardContent>
            </Card>}

            {/* ── Section 8: Sync Settings ──
                NOT shown in the mappingOnly view (Task 22.9). Every toggle here
                writes through PUT /fortnox/settings, which is require_owner and
                is not being widened. In the mapping view the audience is an
                accountant, for whom each switch would flip, 403, toast an error
                and flip back. Hidden rather than disabled: a disabled control
                still says "you could do this if something changed", and nothing
                is going to change. Owners keep the section in full on the full
                Fortnox tab. NOTE: it is not reachable from Integrations either,
                but that predates this change and is not caused by it — the whole
                block from `showMapping` down is already hidden when configOnly is
                set, so Integrations never rendered this section. */}
            {!mappingOnly && <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">
                        {t('settings.fortnox.sync_settings_title')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {settingsLoading ? (
                        <div className="h-8 flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {[
                                ['sync_salary', t('settings.fortnox.sync_salary_label')],
                                ['sync_absences', t('settings.fortnox.sync_absences_label')],
                                ['sync_expenses', t('settings.fortnox.sync_expenses_label')],
                                ['sync_billing', t('settings.fortnox.sync_billing_label')],
                                ['sync_receipts', t('settings.fortnox.sync_receipts_label')],
                            ].map(([key, label]) => (
                                <div key={key} className="flex items-center gap-3">
                                    <Switch
                                        id={`sync-${key}`}
                                        checked={syncSettings[key] ?? false}
                                        onCheckedChange={(val) => handleSyncToggle(key, val)}
                                        data-testid={`sync-toggle-${key}`}
                                    />
                                    <Label htmlFor={`sync-${key}`} className="text-sm cursor-pointer">
                                        {label}
                                    </Label>
                                </div>
                            ))}

                            <div className="pt-2 border-t space-y-1">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="sync-auto"
                                        checked={syncSettings.auto_sync_enabled ?? false}
                                        onCheckedChange={(val) => handleSyncToggle('auto_sync_enabled', val)}
                                        data-testid="sync-toggle-auto"
                                    />
                                    <Label htmlFor="sync-auto" className="text-sm cursor-pointer">
                                        {t('settings.fortnox.auto_sync_label')}
                                    </Label>
                                </div>
                                <p className="text-xs text-muted-foreground ml-10">
                                    {t('settings.fortnox.auto_sync_hint')}
                                </p>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>}
            </>)}
        </div>
    );
}

// Named export for IntegrationsPage — renders only the config sections (1–5),
// not the employee/customer mapping or sync sections.
export function FortnoxConfigTab() {
    return <FortnoxTab configOnly />;
}

/**
 * Admin Settings — one tabbed page consolidating four previously-separate
 * admin-only pages (Projects / Deviations / Employees / Expense Settings).
 * Each tab mounts the existing page component as-is, so their heading +
 * internal state carry over without modification.
 *
 * The individual routes /dashboard/projects, /deviations, /employees,
 * /expense-settings are still wired in App.js for backward-compat with
 * bookmarks; they render the same underlying components.
 */
export default function AdminSettingsPage() {
    const { t } = useTranslation();
    const { isAdmin, isOwner, isAccountant } = useAuth();
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const soloMode = searchParams.get('solo') === 'true'; // hide tab bar when navigated from sidebar
    const [activeTab, setActiveTab] = useState(tabFromUrl || 'projects');

    /**
     * ACCOUNTANT SCOPE (Task 22.9).
     *
     * The route now admits accountants (App.js, `allowAccountant`) so that the
     * Fortnox Mapping link in the top-bar config menu is not a dead link. But
     * WHICH section this page shows is decided by `?tab=`, and that is user
     * input: an accountant let through the door could type `?tab=time-policy`,
     * or drop `&solo=true` to get the whole tab bar, and reach payroll, cost
     * codes, expense rules, notifications, employees and projects.
     *
     * So the route grants entry and this grants nothing further. For an
     * accountant who is not also an admin or owner:
     *   - `effectiveTab` is pinned to fortnox-mapping whatever the URL says,
     *     and whatever any setActiveTab caller below asks for (the Fortnox
     *     OAuth-callback effect calls setActiveTab('fortnox') — pinning here,
     *     downstream of every writer, is what makes that harmless);
     *   - `effectiveSolo` is forced true, so the tab bar cannot be summoned
     *     by removing the solo parameter;
     *   - only the fortnox-mapping <TabsContent> is rendered at all, so no
     *     other section is mounted even momentarily.
     *
     * The out-of-scope `?tab=` is handled by REDIRECTING (coercing to
     * fortnox-mapping) rather than refusing outright, with a visible notice
     * saying so — an accountant following a stale bookmark gets the one page
     * they can use plus an explanation, instead of a dead end. The URL itself
     * is left as typed; the rendered content, not the address bar, is the
     * boundary.
     *
     * `isAdmin` already covers admin/owner/super_admin and `isAccountant` is
     * exclusive (role === "accountant"), so this is empty for every admin and
     * owner: THEY LOSE NOTHING. All three flags are checked anyway so the
     * guard stays correct if a future role is granted more than one of them.
     *
     * This is UI-side defence. The mapping endpoints remain the real boundary.
     */
    const accountantScoped = isAccountant && !isAdmin && !isOwner;
    const effectiveTab = accountantScoped ? ACCOUNTANT_TAB : activeTab;
    const effectiveSolo = accountantScoped ? true : soloMode;
    const redirectedFromDeniedTab =
        accountantScoped && !!tabFromUrl && tabFromUrl !== ACCOUNTANT_TAB;

    // Sync active tab when URL param changes (e.g. navigating from sidebar links)
    useEffect(() => {
        if (tabFromUrl) setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    // Detect OAuth callback from Fortnox — ?fortnox=connected or ?fortnox=error.
    // On connected: show success toast + scrub URL.
    // On error: switch to the fortnox tab so FortnoxTab's own mount-effect can
    // read ?reason= and render the persistent structured Alert. URL is NOT scrubbed
    // here — FortnoxTab scrubs it after reading.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const fortnoxResult = params.get('fortnox');
        if (fortnoxResult === 'connected') {
            setActiveTab('fortnox');
            toast.success(t('settings.fortnox.connected_success'));
            window.history.replaceState({}, '', window.location.pathname);
        } else if (fortnoxResult === 'error') {
            setActiveTab('fortnox');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]);

    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            {!effectiveSolo && (
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('settings.page_title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('settings.page_subtitle')}</p>
                </div>
            )}
            {/* Solo mode header — only for config tabs that don't have their own page header.
                Data tabs (cost-codes, employees, projects, deviations) embed full page
                components that already render their own heading, so we skip ours. */}
            {effectiveSolo && ['time-policy', 'expense-rules', 'notifications', 'fortnox', 'fortnox-mapping'].includes(effectiveTab) && (
                <div className="mb-6">
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {effectiveTab === 'time-policy' && t('settings.tab_payroll_settings')}
                        {effectiveTab === 'expense-rules' && t('settings.tab_expense_rules')}
                        {effectiveTab === 'notifications' && t('settings.tab_notifications')}
                        {effectiveTab === 'fortnox' && t('settings.tab_fortnox')}
                        {effectiveTab === 'fortnox-mapping' && t('settings.fortnox.employee_mapping_title')}
                    </h1>
                </div>
            )}

            {/* Said out loud rather than jumping silently: an accountant who
                followed a link to another settings section is told why they
                landed here instead (Task 22.9). */}
            {redirectedFromDeniedTab && (
                <Alert data-testid="settings-accountant-scope-notice">
                    <AlertTitle>{t('settings.accountant_scope_title')}</AlertTitle>
                    <AlertDescription>{t('settings.accountant_scope_body')}</AlertDescription>
                </Alert>
            )}

            <Tabs value={effectiveTab} onValueChange={setActiveTab} className="space-y-4">
                {/* Tab bar hidden in solo mode — sidebar nav links open focused
                    single-section views. `effectiveSolo`, not `soloMode`, so an
                    accountant cannot summon the full bar by dropping
                    &solo=true from the URL (Task 22.9). */}
                {!effectiveSolo && (
                    <TabsList className="grid w-full grid-cols-8">
                        <TabsTrigger value="projects" data-testid="tab-settings-projects">
                            <FolderKanban className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_projects')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="cost-codes" data-testid="tab-settings-cost-codes">
                            <Tags className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_cost_codes')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="deviations" data-testid="tab-settings-deviations">
                            <AlertCircle className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_deviations')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="employees" data-testid="tab-settings-employees">
                            <Users className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_employees')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="expense-rules" data-testid="tab-settings-expense-rules">
                            <SettingsIcon className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_expense_rules')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="notifications" data-testid="tab-settings-notifications">
                            <Mail className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_notifications')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="time-policy" data-testid="tab-settings-time-policy">
                            <Clock className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_payroll_settings')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="fortnox" data-testid="tab-settings-fortnox">
                            <Link2 className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">{t('settings.tab_fortnox')}</span>
                        </TabsTrigger>
                    </TabsList>
                )}

                {/* Every admin-only section is not merely inactive for an
                    accountant — it is not in the tree at all, so no `?tab=`
                    value can select one and none of them mounts or fetches
                    (Task 22.9). Admins and owners see exactly what they saw
                    before: `accountantScoped` is false for every one of them. */}
                {!accountantScoped && (
                    <>
                        <TabsContent value="projects">
                            <ProjectsPage />
                        </TabsContent>
                        <TabsContent value="cost-codes">
                            <CostCodesPage />
                        </TabsContent>
                        <TabsContent value="deviations">
                            <DeviationsPage />
                        </TabsContent>
                        <TabsContent value="employees">
                            <EmployeesPage />
                        </TabsContent>
                        <TabsContent value="expense-rules">
                            <ExpenseSettingsPage />
                        </TabsContent>
                        <TabsContent value="notifications">
                            <EmailNotificationsPage />
                        </TabsContent>
                        <TabsContent value="time-policy">
                            <TimePolicyTab />
                        </TabsContent>
                        <TabsContent value="fortnox">
                            <FortnoxTab configOnly />
                        </TabsContent>
                    </>
                )}
                <TabsContent value="fortnox-mapping">
                    {/* readOnly for the accountant only — both mapping PUTs are
                        require_owner, so their Save buttons would 403 (Task
                        22.9). False for every admin and owner. */}
                    <FortnoxTab mappingOnly readOnly={accountantScoped} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
